import { generatePath as generatePathBase } from 'react-router-dom';
import { stringifyPathParams } from './path-params';
import type { PathParams } from './path-params';
import type { Route } from './routes';

export function generatePath<Path extends Route>(
  path: Path,
  params: PathParams<Path>,
  query?: Record<string, string | number | boolean | undefined | null>,
  hash?: string,
): string {
  const base = generatePathBase(path, stringifyPathParams(params));

  const searchParams = new URLSearchParams();
  if (query != null) {
    for (const [key, value] of Object.entries(query)) {
      if (value != null) {
        searchParams.set(key, String(value));
      }
    }
  }

  const search = searchParams.toString();
  const withSearch = search.length > 0 ? `${base}?${search}` : base;
  return hash != null ? `${withSearch}#${hash}` : withSearch;
}

/**
 * Regex patterns for Hydra-only URLs that don't require loading an app bundle.
 *
 * When adding a new Hydra-only route:
 * 1. Add the route to main.tsx
 * 2. Add a regex pattern here to match the route
 *
 * Ember can check if a URL belongs to Hydra by calling window.__hydra__.isHydraRoute(path).
 */
const HYDRA_ONLY_URLS_REGEX = [
  /^\/workspaces\/(last|(\d+))\/settings\/ai-business-profile/i,
  /^\/workspaces\/(last|(\d+))\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/settings\/api-keys/i,
  /^\/workspaces\/(last|(\d+))\/settings\/auto_archive/i,
  /^\/workspaces\/(last|(\d+))\/settings\/suppressions/i,
  /^\/workspaces\/(last|(\d+))\/settings\/actions\/sinch/i,
  /^\/settings\/api-keys/i,
  /^\/settings\/audit-logs/i,
  /^\/workspaces\/(last|(\d+))\/agent/i,
  /^\/workspaces\/(last|(\d+))\/tasks/i, // redirect-only — kept so Ember forwards to Hydra's router
  /^\/workspaces\/(last|(\d+))\/analysis\/deliverability/i,
  /^\/workspaces\/(last|(\d+))\/health/i,
  /^\/workspaces\/(last|(\d+))\/home/i, // covers /home and all sub-routes including /home/add-people
  /^\/workspaces\/(last|(\d+))\/pipelines/i, // native — no longer an embedded app bundle
  /^\/workspaces\/(last|(\d+))\/geofences/i,
  /^\/workspaces\/(last|(\d+))\/snippets/i, // list, new, detail — migrated from Journeys to Hydra
  /^\/workspaces\/(last|(\d+))\/journeys\/snippets/i, // legacy URL — redirect target inside Hydra's router
  /^\/workspaces\/(last|(\d+))\/assets/i, // asset library — migrated from Journeys to Hydra
  /^\/workspaces\/(last|(\d+))\/journeys\/assets/i, // legacy URL — redirect target inside Hydra's router
];

/**
 * Checks if a URL path matches a Hydra-only route.
 *
 * @example
 * isHydraRoute('/workspaces/123/agent') // true
 * isHydraRoute('/workspaces/last/settings/ai-business-profile') // true
 * isHydraRoute('/workspaces/123/foo') // false
 */
export function isHydraRoute(path: string): boolean {
  return HYDRA_ONLY_URLS_REGEX.some((regex) => regex.test(path));
}
